/* =============================================================================
   VIEWS / ADMIN / ARCHIVE-ORDERS — Orders-Sub-Section (S68 REF-2)
   =============================================================================
   Extrahiert aus archive.js (Sub-Section orders).

   Exports:
     - loadOrders                 Async-Loader für ordersCache
     - getFilteredOrders          Filter+Sort des ordersCache
     - renderOrdersArchive        Sub-Section-Renderer
     - renderOrderStatusBadge     Badge-Helper
     - exportArchiveOrdersCSV     CSV-Exporter

   Source-of-Truth: storage.orders.listForRange (DB-frisch, NICHT state-cache —
   Memory `feedback_gg_export_fresh_db`).
============================================================================ */

import { htmlEscape, fmtDate, fmtEUR, showToast } from '../../utils/helpers.js';
import { _state, getState } from './archive-state.js';
import { _renderImmutabilityNote, downloadCsv } from './archive-helpers.js';
import { renderOperatorBadges } from './operator-ui.js';

/** Lädt Bestellungen aus DB für den aktuellen Zeitraum. Ruft `window.renderArchive`
 *  vor + nach dem Fetch (Loading-Skeleton + finaler Render). */
export async function loadOrders() {
  _state.ordersLoading = true;
  if (typeof window.renderArchive === 'function') window.renderArchive();
  try {
    const storage = window.App && window.App.storage && window.App.storage.orders;
    if (!storage || typeof storage.listForRange !== 'function') {
      console.warn('[archive] orders.listForRange nicht verfügbar — fallback auf state-cache');
      const state = getState();
      _state.ordersCache = (state.orders || []).filter(o => {
        if (!o.pickupDate) return false;
        return o.pickupDate >= _state.dateFrom && o.pickupDate <= _state.dateTo;
      });
    } else {
      _state.ordersCache = await storage.listForRange(_state.dateFrom, _state.dateTo);
    }
  } catch (e) {
    console.error('[archive] loadOrders failed:', e);
    _state.ordersCache = [];
  } finally {
    _state.ordersLoading = false;
    if (typeof window.renderArchive === 'function') window.renderArchive();
  }
}

/** Liefert die archive-gefilterte Bestell-Liste oder null wenn noch nicht geladen. */
export function getFilteredOrders() {
  if (_state.ordersCache === null) return null;
  const q = _state.search.trim().toLowerCase();
  let list = _state.ordersCache.slice();

  if (_state.status !== 'all') {
    list = list.filter(o => o.status === _state.status);
  }
  if (q) {
    list = list.filter(o =>
      (o.customerName && o.customerName.toLowerCase().includes(q)) ||
      (o.customerEmail && o.customerEmail.toLowerCase().includes(q)) ||
      (o.customerPhone && o.customerPhone.toLowerCase().includes(q)) ||
      (String(o.orderNumber || '').toLowerCase().includes(q)) ||
      (String(o.invoiceNumber || '').toLowerCase().includes(q))
    );
  }
  list.sort((a, b) => {
    const dk = (b.pickupDate || '').localeCompare(a.pickupDate || '');
    if (dk !== 0) return dk;
    return (b.pickupTime || '').localeCompare(a.pickupTime || '');
  });
  return list;
}

export function renderOrdersArchive() {
  if (_state.ordersLoading) {
    return `
      <div class="archive-meta">⏳ Lade Bestellungen aus der Datenbank…</div>
      <div class="archive-table-wrap">
        ${Array.from({ length: 6 }).map(() =>
          '<div class="skeleton skeleton-line lg" style="margin: 6px 0;"></div>'
        ).join('')}
      </div>
    `;
  }
  const list = getFilteredOrders();
  if (list === null) {
    return `<div class="empty"><div class="empty-emoji">⏳</div>Klick auf den Tab, um Bestellungen zu laden.</div>`;
  }

  if (list.length === 0) {
    return `<div class="empty"><div class="empty-emoji">📭</div>Keine Bestellungen im gewählten Zeitraum gefunden.</div>`;
  }

  // S59 Block H — Pagination.
  const pgO = (window.PaginatedList && window.PaginatedList.paginate)
    ? window.PaginatedList.paginate({
        items: list, containerId: 'archive-orders-pager',
        initial: 10, step: 10, rendererFn: 'renderArchive'
      })
    : { slice: list.slice(0, 10), moreButtonHtml: '', visible: Math.min(10, list.length), total: list.length };

  const rowsHtml = pgO.slice.map(o => `
    <tr class="archive-row" onclick="openOrderDetail('${htmlEscape(o.id)}')">
      <td>${fmtDate(o.pickupDate)}<br><span style="color: var(--ink-muted); font-size:.85em;">${htmlEscape(o.pickupTime || '')}</span></td>
      <td><strong>${htmlEscape(o.customerName || '—')}</strong><br><span style="color: var(--ink-muted); font-size:.85em;">#${htmlEscape(o.orderNumber || o.id.slice(0, 6))}</span></td>
      <td>${o.invoiceNumber ? '<strong>Bon ' + htmlEscape(o.invoiceNumber) + '</strong>' : '—'}</td>
      <td>${fmtEUR((Number(o.totalGrossCents) || 0) / 100)}</td>
      <td>${renderOrderStatusBadge(o)}</td>
      <td>${renderOperatorBadges(o) || '<span style="color:var(--ink-muted);">—</span>'}</td>
    </tr>
  `).join('');

  return `
    <div class="archive-meta">
      <span>
        ${list.length} Bestellung${list.length === 1 ? '' : 'en'} gefunden
        · zeige ${pgO.visible} von ${pgO.total}
      </span>
      <button class="btn-archive-reload" onclick="exportArchiveOrdersCSV()"
              title="Gefilterte Bestellungen als CSV herunterladen"
              ${list.length === 0 ? 'disabled style="opacity:.45;cursor:not-allowed;"' : ''}>
        📥 CSV-Export
      </button>
      <button class="btn-archive-reload" onclick="setArchiveSection('orders')" title="Daten frisch aus der Datenbank ziehen">↻ Neu laden</button>
    </div>
    <div class="archive-table-wrap">
      <table class="archive-table">
        <thead>
          <tr>
            <th>Datum / Abholung</th>
            <th>Kunde / Bestell-Nr.</th>
            <th>Bon-Nr.</th>
            <th>Brutto</th>
            <th>Status</th>
            <th>Operator</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
      </table>
    </div>
    ${pgO.moreButtonHtml}
    ${_renderImmutabilityNote('GoBD §147 AO · 10 Jahre Aufbewahrungspflicht')}
  `;
}

export function renderOrderStatusBadge(o) {
  const map = {
    picked_up:  { l: 'Abgeholt',  c: 'completed' },
    cancelled:  { l: 'Storniert', c: 'declined' },
    no_show:    { l: 'No-Show',   c: 'declined' },
    pending:    { l: 'Offen',     c: 'pending' },
    confirmed:  { l: 'Bestätigt', c: 'confirmed' },
    preparing:  { l: 'In Zubereitung', c: 'pending' },
    ready:      { l: 'Bereit',    c: 'confirmed' }
  };
  const m = map[o.status] || { l: o.status, c: 'completed' };
  let badge = `<span class="badge badge-${m.c}">${m.l}</span>`;
  if (o.isCorrection) badge += ' <span class="badge badge-declined">Stornobeleg</span>';
  return badge;
}

/** Bestellungen-Archiv → CSV. */
export function exportArchiveOrdersCSV() {
  const list = getFilteredOrders();
  if (!list || list.length === 0) { showToast('Keine Bestellungen im Filter — nichts zu exportieren', 'info'); return; }

  const header = [
    'ID', 'Bestell-Nr.', 'Bon-Nr.', 'Status', 'Korrekturbeleg',
    'Abhol-Datum', 'Abhol-Zeit', 'Kunde', 'E-Mail', 'Telefon',
    'Brutto (EUR)', 'Netto (EUR)', 'MwSt 7% (EUR)', 'MwSt 19% (EUR)',
    'Bezahlart', 'Bezahlt am', 'Erstellt am',
    'Aufgenommen von', 'Letzte Aktion von'
  ];
  const rows = [header];

  const cToE = (cents) => {
    const v = (Number(cents) || 0) / 100;
    return v.toFixed(2).replace('.', ',');
  };

  for (const o of list) {
    rows.push([
      o.id || '',
      o.orderNumber || o.order_number || '',
      o.invoiceNumber || o.invoice_number || '',
      o.status || '',
      o.isCorrection ? 'ja' : 'nein',
      o.pickupDate || '',
      o.pickupTime || '',
      o.customerName || '',
      o.customerEmail || '',
      o.customerPhone || '',
      cToE(o.totalGrossCents || o.total_gross_cents),
      cToE(o.subtotalNetCents || o.subtotal_net_cents),
      cToE(o.tax7Cents || o.tax_7_cents),
      cToE(o.tax19Cents || o.tax_19_cents),
      o.paymentMethod || o.payment_method || '',
      o.paidAt || o.paid_at || '',
      o.createdAt || o.created_at || '',
      o.createdByOperatorName || o.created_by_operator_name || '',
      o.lastActionOperatorName || o.last_action_operator_name || ''
    ]);
  }
  const ts = `${_state.dateFrom}_bis_${_state.dateTo}`;
  downloadCsv(rows, `bestellungen-archiv-${ts}.csv`);
  showToast(`${list.length} Bestellung${list.length === 1 ? '' : 'en'} exportiert`, 'success');
}
